import { boolean, number, object, string } from "yup";
import { BankAccountRowItem, InvestmentEntity } from "../types";

export type VerificationStatus = "VERIFIED" | "UNVERIFIED";

/**
 * Yup validation schema for the BankAccountConfiguration slideout form.
 * Uses conditional validation - bank mapping is only required when the provider is enabled.
 */
export const bankAccountConfigurationSchema = object().shape({
  selectedEntityId: number().nullable().defined(),

  fidesEnabled: boolean().required(),
  fidesBankMapping: string()
    .nullable()
    .when("fidesEnabled", (fidesEnabled: boolean[], schema) =>
      fidesEnabled[0]
        ? schema.required("Bank mapping is required when Fides is enabled")
        : schema.nullable(),
    )
    .defined(),
  fidesVerificationStatus: string().oneOf(["VERIFIED", "UNVERIFIED"]).required(),

  rpaEnabled: boolean().required(),
  rpaBankMapping: string()
    .nullable()
    .when("rpaEnabled", (rpaEnabled: boolean[], schema) =>
      rpaEnabled[0]
        ? schema.required("Bank mapping is required when RPA is enabled")
        : schema.nullable(),
    )
    .defined(),

  fundRecsEnabled: boolean()
    .required()
    .when(["fidesEnabled", "rpaEnabled"], {
      is: (fidesEnabled: boolean, rpaEnabled: boolean) => !fidesEnabled && !rpaEnabled,
      then: (schema) => schema.oneOf([false], "FundRecs requires Fides or RPA to be enabled"),
    }),
  fundRecsUdf: string()
    .defined()
    .default("")
    .when("fundRecsEnabled", (fundRecsEnabled: boolean[], schema) =>
      fundRecsEnabled[0]
        ? schema
            .required("UDF is required when FundRecs is enabled")
            .trim()
            .min(1, "UDF is required when FundRecs is enabled")
        : schema.notRequired(),
    ),
});

export interface ProviderBankSource {
  providerId: number;
  providerName: string;
  bankSource: string;
  capabilityIds: number[];
}

export interface SlideoutFormState {
  selectedEntityId: number | null;

  fidesEnabled: boolean;
  fidesBankMapping: string | null;
  fidesVerificationStatus: VerificationStatus;

  rpaEnabled: boolean;
  rpaBankMapping: string | null;

  fundRecsEnabled: boolean;
  fundRecsUdf: string;
}

export interface BankAccountConfigurationSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  bankAccount: BankAccountRowItem | null;
  onSave?: (formState: SlideoutFormState) => void;
  /**
   * Callback invoked when entity association is updated via the entity modal.
   * Receives the updated entity info so the parent can update local state.
   * Use this to trigger a data refresh and update the slideout display.
   */
  onEntitySave?: (entity: { entityId: number; entityName: string }) => void;
  /**
   * Arena ID for fetching available entities. Required for entity association modal.
   *
   * IMPORTANT: This MUST be the user's current session arena ID (the same arena
   * that the GraphQL request context uses). If you pass a different arena ID,
   * the entity mapping mutation will fail because the mutation uses the request
   * context arena to look up entities.
   */
  arenaId?: number | null;
  /** Arena domain for building entity URLs (e.g., "rockstonecap") */
  arenaDomain?: string;
}

export interface AccountDetailsSectionProps {
  bankAccount: BankAccountRowItem;
  onEditEntity: () => void;
  /** Function to generate entity URL for GPX portal links. Returns undefined when the arena domain is unavailable, in which case no link is rendered. */
  getEntityUrl?: (entityId: number) => string | undefined;
  /** Optional override for the reporting-only entity, used to reflect a save without re-initialising Formik. */
  reportingEntityOverride?: InvestmentEntity | null;
}

export interface FidesConfigurationProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  bankMapping: string | null;
  onBankMappingChange: (bankSource: string | null) => void;
  verificationStatus: VerificationStatus;
  onVerificationStatusChange: (status: VerificationStatus) => void;
  availableBankSources: ProviderBankSource[];
}

export interface RpaConfigurationProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  bankMapping: string | null;
  onBankMappingChange: (bankSource: string | null) => void;
  availableBankSources: ProviderBankSource[];
}

export interface FundRecsConfigurationProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  udfValue: string;
  onUdfChange: (value: string) => void;
  hasInvestmentEntity: boolean;
}

export interface ConfigurationSectionProps {
  fidesEnabled: boolean;
  onFidesEnabledChange: (enabled: boolean) => void;
  fidesBankMapping: string | null;
  onFidesBankMappingChange: (bankSource: string | null) => void;
  fidesVerificationStatus: VerificationStatus;
  onFidesVerificationChange: (status: VerificationStatus) => void;
  fidesBankSources: ProviderBankSource[];
  fidesBankMappingError?: string;

  rpaEnabled: boolean;
  onRpaEnabledChange: (enabled: boolean) => void;
  rpaBankMapping: string | null;
  onRpaBankMappingChange: (bankSource: string | null) => void;
  rpaBankSources: ProviderBankSource[];
  rpaBankMappingError?: string;

  fundRecsEnabled: boolean;
  onFundRecsEnabledChange: (enabled: boolean) => void;
  fundRecsUdf: string;
  onFundRecsUdfChange: (value: string) => void;
  fundRecsUdfError?: string;
  fundRecsEnabledError?: string;

  /** Whether the providers data is still loading */
  isLoadingProviders?: boolean;
}
